import NiceModal from '@ebay/nice-modal-react';
import { SnackbarProviderWrap } from '@/providers/SnackbarProvider';
import { AuthProvider } from '@/providers/AuthProvider';
import { ThemeProviderWrap } from '@/providers/ThemeProvider';
import { OverlayProvider } from '@/providers/OverlayProvider';
import CircularProgress from '@mui/material/CircularProgress';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { ja } from 'date-fns/locale';
import { Suspense } from 'react';
import type { PropsWithChildren } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// NOTE: 4xx はリトライしない。既定の retry:3 のままだと、権限不足(403)の
// ように再試行しても結果が変わらない失敗でも指数バックオフで3回待ってから
// エラー表示に至り、利用者は数秒スピナーを見続ける(#666)。5xx と通信断は
// 再試行に意味があるため既定の回数を維持する。
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        const status = (error as { status?: number } | null)?.status;
        if (status != null && status >= 400 && status < 500) return false;
        return failureCount < 3;
      },
    },
  },
});

export const AppProvider: React.FC<PropsWithChildren> = ({ children }) => (
  <Suspense fallback={<CircularProgress />}>
    <QueryClientProvider client={queryClient}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
        <ThemeProviderWrap>
          <OverlayProvider>
            <AuthProvider>
              <SnackbarProviderWrap>
                <NiceModal.Provider>{children}</NiceModal.Provider>
              </SnackbarProviderWrap>
            </AuthProvider>
          </OverlayProvider>
        </ThemeProviderWrap>
      </LocalizationProvider>
    </QueryClientProvider>
  </Suspense>
);
